'use client';

import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useRef, useEffect } from 'react';

import { loadCustomerInfo } from '@/lib/localStorage';
import { usePickupDateTimeStore } from '@/stores/pickup-datetime-store';
import { useReservationStore } from '@/stores/reservation-store';

export default function ReservationForm() {
  const { formData, updateFormData } = useReservationStore();
  const { pickupDate, pickupTime } = usePickupDateTimeStore();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load customer info from localStorage on mount
  useEffect(() => {
    const savedInfo = loadCustomerInfo();
    if (savedInfo) {
      updateFormData({
        name: savedInfo.name,
        phone: savedInfo.phone,
      });
    }
  }, [updateFormData]);

  // pickupDate를 formData에 설정
  useEffect(() => {
    if (pickupDate) {
      updateFormData({ pickupDate });
    }
  }, [pickupDate, updateFormData]);

  const handleInputChange = (field: string, value: string | Date | undefined) => {
    updateFormData({ [field]: value });
  };

  const handlePhoneChange = (value: string) => {
    // 숫자만 추출
    const numbersOnly = value.replace(/\D/g, '');

    // 최대 11자리까지만 허용
    const limitedNumbers = numbersOnly.slice(0, 11);

    // 하이픈 자동 추가
    let formatted = limitedNumbers;
    if (limitedNumbers.length > 3) {
      formatted = limitedNumbers.slice(0, 3) + '-' + limitedNumbers.slice(3);
    }
    if (limitedNumbers.length > 7) {
      formatted = limitedNumbers.slice(0, 3) + '-' + limitedNumbers.slice(3, 7) + '-' + limitedNumbers.slice(7);
    }

    updateFormData({ phone: formatted });
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [formData.requirements]);

  return (
    <div className="px-5 pt-5">
      <h2 className="text-xl font-semibold text-[#1F1F1F] mb-5">예약자 정보</h2>

      <div className="space-y-[41px]">
        {/* 이름 */}
        <div className="space-y-[10px] focus-within:text-[#3D8AFF]">
          <div className="flex items-center gap-[1px]">
            <span className="text-lg font-semibold text-[#454545]">이름</span>
            <span className="text-base font-semibold text-[#F67A7A]">*</span>
          </div>
          <div className="border-b border-[#D9D9D9] focus-within:border-[#3D8AFF] pb-2">
            <input
              type="text"
              value={formData.name}
              onChange={e => handleInputChange('name', e.target.value)}
              className="w-full bg-transparent text-md text-[#1F1F1F] placeholder-[#999] focus:outline-none"
              placeholder="김공세"
            />
          </div>
        </div>

        {/* 휴대폰 번호 */}
        <div className="space-y-[10px] focus-within:text-[#3D8AFF]">
          <div className="flex items-center gap-[1px]">
            <span className="text-lg font-semibold text-[#454545]">휴대폰 번호</span>
            <span className="text-base font-semibold text-[#F67A7A]">*</span>
          </div>
          <div className="border-b border-[#D9D9D9] focus-within:border-[#3D8AFF] pb-2">
            <input
              type="tel"
              value={formData.phone}
              onChange={e => handlePhoneChange(e.target.value)}
              className="w-full bg-transparent text-md text-[#1F1F1F] placeholder-[#999] focus:outline-none"
              placeholder="010-0000-0000"
            />
          </div>
        </div>

        {/* 픽업시간 */}
        <div className="space-y-[10px]">
          <div className="flex items-center gap-[1px]">
            <span className="text-lg font-semibold text-[#454545]">픽업시간</span>
            <span className="text-base font-semibold text-[#F67A7A]">*</span>
          </div>
          <div className="py-[6px]">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-base font-medium text-gray-700">
                {pickupDate && pickupTime
                  ? `${format(pickupDate, 'yyyy년 MM월 dd일 (E)', {
                      locale: ko,
                    })} ${pickupTime}`
                  : '픽업시간이 선택되지 않았습니다'}
              </p>
            </div>
          </div>
        </div>

        {/* 요청사항 */}
        <div className="space-y-[10px] focus-within:text-[#3D8AFF]">
          <div>
            <span className="text-lg font-semibold text-[#454545]">요청사항</span>
          </div>
          <div className="border-b border-[#D9D9D9] focus-within:border-[#3D8AFF] pb-1">
            <textarea
              ref={textareaRef}
              value={formData.requirements}
              onChange={e => {
                handleInputChange('requirements', e.target.value);
                adjustTextareaHeight();
              }}
              className="w-full bg-transparent text-md text-[#1F1F1F] placeholder-[#999] focus:outline-none resize-none overflow-hidden"
              placeholder="선불권 사용은 요청사항으로 알려주세요"
              rows={1}
              style={{ minHeight: '1.5rem' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
